import { useAtomValue } from 'jotai/index';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { HorizontalContainer, VerticalContainer } from '@/components';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import { shouldWarnAboutStart } from '../lib/shouldWarnAboutStart';
import { useCaregiverPhoneCallConfirmation } from '../lib/useCaregiverPhoneCallConfirmation';
import { isConnectingAtom, isHandledByOtherAtom } from '../model/store';
import { AiJournalNoteNotSupportedView } from './AiJournalNoteNotSupportedView';
import { HandledByOtherView } from './HandledByOtherView';
import { InfoTable } from './InfoTable';
import { PhoneCallActiveView } from './PhoneCallActiveView';
import { PhoneCallButton } from './PhoneCallButton';

type PhoneCallConfirmationViewProps = {
    consultationUid: string;
    onClose: (isAutoClosed?: boolean) => void;
    onPhoneCallCompleted: () => void;
};

const VALID_VERIFICATION_TEXTS = ['yes', 'ja'];

export const PhoneCallConfirmationView = ({
    consultationUid,
    onClose,
    onPhoneCallCompleted,
}: PhoneCallConfirmationViewProps) => {
    const [verificationText, setVerificationText] = useState('');

    const { confirmation, phoneCallStatus, startCall, hangUpPhoneCall } =
        useCaregiverPhoneCallConfirmation({
            consultationUid,
            onClose,
        });

    const isConnecting = useAtomValue(isConnectingAtom);

    const isHandledByOther = useAtomValue(isHandledByOtherAtom);

    const phoneCallState = phoneCallStatus
        ? phoneCallStatus === 'Disconnected'
            ? 'completed'
            : 'active'
        : 'idle';

    useEffect(() => {
        if (phoneCallState !== 'completed') return;

        // Consultation was already finished (e.g. an earlier call already ended it) -
        // asking again whether to end it doesn't make sense.
        if (confirmation?.Consultation.FinishedOn) {
            onClose(true);
            return;
        }

        onPhoneCallCompleted();
    }, [phoneCallState, confirmation, onClose, onPhoneCallCompleted]);

    const isVerificationTextValid = useMemo(
        () =>
            VALID_VERIFICATION_TEXTS.includes(
                verificationText.trim().toLowerCase(),
            ),
        [verificationText],
    );

    const isButtonDisabled = isHandledByOther && !isVerificationTextValid;

    const handleCallClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (!isButtonDisabled) {
                startCall();
            }
        },
        [isButtonDisabled, startCall],
    );

    const handleEndCall = useCallback(() => {
        hangUpPhoneCall();
    }, [hangUpPhoneCall]);

    return (
        <VerticalContainer>
            <VerticalContainer spacing="none">
                <p>{R.PhoneCallSubtitle}</p>
                <InfoTable
                    fields={confirmation?.FormattedFields}
                    shouldWarnAboutStart={shouldWarnAboutStart(
                        confirmation?.Consultation.ScheduledOn,
                    )}
                />
            </VerticalContainer>

            {isHandledByOther && (
                <HandledByOtherView
                    verificationText={verificationText}
                    setVerificationText={setVerificationText}
                />
            )}
            {phoneCallState === 'idle' && (
                <VerticalContainer>
                    <AiJournalNoteNotSupportedView />
                    <HorizontalContainer className="justify-end">
                        <PhoneCallButton
                            isLoading={isConnecting}
                            onClick={handleCallClick}
                            disabled={isButtonDisabled}
                        />
                    </HorizontalContainer>
                </VerticalContainer>
            )}

            {phoneCallState === 'active' && (
                <PhoneCallActiveView
                    onEndCall={handleEndCall}
                    callStatus={phoneCallStatus}
                />
            )}
        </VerticalContainer>
    );
};
